import * as React from 'react';
import styled from 'styled-components';

import { useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import { useInterval } from '../../../utils/hooks/useInterval';
import { CardLine } from '../../ui/divider/CardLine';
import { CardSticker } from '../CardSticker';
import { CardLink } from '../CardWrapper';
import { AssignmentType, Estate, SearchEstateSort, useSearchEstateQuery } from '../../../__types__/generated/cluster';
import { client } from '../../../../client/__graphql__/clusterClient';
import { env } from '../../../__config__/env';
import { Area } from '../Area';
import Price from '../Price';

const PropertyHighLightedStyle = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 600px;
  margin: 0 auto 40px;

  @media all and (max-width: 599px) {
    height: max-content;
    flex-direction: column;
  }

  .link {
    @media all and (max-width: 599px) {
      width: 100%;
    }
  }
`;

const SlideMobile = styled.div`
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: relative;
  width: 100%;
  height: 300px;

  @media all and (min-width: 600px) {
    height: 100%;
  }
`;

/* Kopiert css fra PropertyCarousel, slik at den er selvstendig */
const PropertyHighLightedCardStyle = styled.div`
  position: relative;
  width: 100%;

  color: ${({ theme }) => theme.colors.black};

  @media all and (min-width: 600px) {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 30px;
    width: 300px;
    height: max-content;
    margin: auto;
  }

  background-color: ${({ theme }) => theme.colors.white};
  text-align: center;
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 60px 30px 30px;
  box-shadow: 0 0 20px rgb(0, 0, 0, 0.2);

  @media all and (max-width: 420px) {
    width: 100%;
    top: auto;
    left: auto;
  }
`;

const Slide = styled.div`
  transition: opacity 0.5s ease-in-out;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: absolute;
  width: 100%;
  opacity: 0;

  &.show-slide {
    opacity: 1;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border: none;
    cursor: pointer;
  }
`;

interface Image {
  id: string | number;
  loaded: boolean;
  url: string;
  description?: string;
  location?: string;
  property_id: number;
  sold: boolean;
}

export const PropertyHighLightedCarousel: React.FC = () => {
  const params = useParams<{ urltag: string }>();
  const init = React.useRef<any>(true);

  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [currentPropertyId, setCurrentPropertyId] = React.useState<number>(0);

  const [images, setImages] = React.useState<Image[]>([]);

  const { data, loading } = useSearchEstateQuery({
    client: client,
    fetchPolicy: 'network-only',
    skip: !params.urltag,
    variables: {
      input: {
        brandId: env.BRAND_ID
      },
      filter: {
        sort: SearchEstateSort.PublishedNewOld,
        assignmentTypes: [AssignmentType.NewDevelopment, AssignmentType.NewDevelopmentCommercial, AssignmentType.NewDevelopmentLeisure],
        tags: [params.urltag],
        sold: false,
        isProjectUnit: false,
        published: true,
        available: true,
        pagination: {
          limit: 5
        }
      }
    }
  });

  React.useEffect(() => {
    if (data?.search.estate && data?.search.estate?.list?.length > 0) {
      data?.search?.estate.list.map((item: any, index: any) => {
        if (!item) return null;
        const estate: Estate = item as Estate;
        setImages((prev: any) => [
          ...prev,
          {
            loaded: false,
            id: `img_${index}`,
            url: estate?.images?.main?.url ? estate?.images?.main?.url : '',
            property_id: estate?.assignmentNumber,
            sold: estate?.sold
          }
        ]);
      });
    }
  }, [data]);

  React.useEffect(() => {
    if (images.length > 0 && init.current) {
      init.current = false;
      lazyLoad(0);
    }
  }, [, images]);

  useInterval({
    cb: () => {
      const imageIndex = getNextImage(currentImageIndex, images);
      setCurrentImageIndex(imageIndex);
      if (imageIndex > -1 && images[imageIndex]?.property_id) {
        setCurrentPropertyId(images[imageIndex].property_id);
      }
      lazyLoad(imageIndex);
    },
    delay: 5500
  });

  const getNextImage = (index: number, images: any[]) => {
    if (images.length - 1 === index) {
      return 0;
    }
    return index + 1;
  };

  const lazyLoad = (index: number) => {
    setCurrentPropertyId(images[index]?.property_id);
    setImages((prev) => {
      return prev.map((item, imageIndex) => {
        if (index === imageIndex || index + 1 === imageIndex) {
          return { ...item, loaded: true };
        } else {
          return item;
        }
      });
    });
  };

  const ImageClick_v2 = () => {
    if (currentPropertyId && currentPropertyId > 0) {
      window.open(`https://privatmegleren.no/` + currentPropertyId, '_blank');
    }
  };

  if (images.length === 0 && !loading) {
    return null;
  }

  return (
    <>
      <PropertyHighLightedStyle>
        <SlideMobile>
          {images.map((item, index) => {
            return (
              <Slide key={index} className={index === currentImageIndex ? 'show-slide' : ''}>
                <img data-src={item.url} src={item.loaded ? item.url : ''} alt="" onClick={ImageClick_v2} />
                <CardSticker sold={item.sold} />
              </Slide>
            );
          })}
        </SlideMobile>
        {data?.search?.estate?.list.map((item: any, index: any) => {
          if (!item) return null;
          const estate: Estate = item as Estate;
          if (index === currentImageIndex) {
            return (
              <CardLink
                key={`highlight_link_${index}`}
                target="_blank"
                className="link"
                as="a"
                href={`https://privatmegleren.no/${estate.assignmentNumber}`}
              >
                <PropertyHighLightedCardStyle key={`highlight_${index}`}>
                  <h4>{estate?.postalPlace}</h4>
                  <CardLine />
                  <p>
                    {estate?.streetAddress}
                    <br />
                    {estate?.type} - <Area size={estate.size} /> <br />
                    <Price prices={estate.prices} />
                  </p>
                </PropertyHighLightedCardStyle>
              </CardLink>
            );
          }
          return null;
        })}
      </PropertyHighLightedStyle>
    </>
  );
};
